<template>
    <div
        v-if="visible"
        class="flex flex-col items-start gap-6 md:flex-row"
    >
        <div class="flex w-full flex-col md:w-1/2">
            <div class="flex w-full flex-1">
                <prime-text
                    id="booking_id"
                    v-model="bookingId"
                    :placeholder="bookingIdName"
                    class="w-full"
                    :pt="{
                        root: {
                            class:
                                bookingIdErrorMessage !== ''
                                    ? 'border-red-500'
                                    : ''
                        }
                    }"
                    :pt-options="{
                        mergeSections: true,
                        mergeProps: true
                    }"
                    @keydown.enter="handleAddCustomerByBookingId"
                />

                <div class="flex">
                    <prime-button
                        :loading="addCustomerByBookingIdLoading || isPending"
                        label="Add"
                        @click="handleAddCustomerByBookingId"
                    />

                    <prime-button
                        outlined
                        :disabled="addCustomerByBookingIdLoading || isPending"
                        label="Cancel"
                        @click="handleCancel"
                    />
                </div>
            </div>

            <div class="flex items-center">
                <label
                    for="booking_id"
                    class="sr-only"
                >
                    {{ bookingIdName }}
                </label>

                <span
                    v-if="bookingIdErrorMessage !== ''"
                    class="font-medium text-danger-600"
                >
                    {{ bookingIdErrorMessage }}
                </span>
            </div>
        </div>

        <div class="text-base md:w-1/2">
            Your friend or family can find their {{ bookingIdName }} on their
            profile summary. Suggested Friends and Family can be found
            <nuxt-link :to="profileFriendsAndFamilyLink()"
                ><prime-button
                    link
                    class="!p-0"
                    label="here" /></nuxt-link
            >.
        </div>
    </div>
    <confirm-dialog
        group="enrollmentAddByBookingId"
        :pt="{
            root: {
                class: '!w-[400px]'
            },
            footer: {
                class: 'space-x-4'
            },
            rejectButton: {
                outlined: true
            }
        }"
        :pt-options="{
            mergeProps: true
        }"
    />
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const EnrollmentAddByBookingIdAddSavedParticipantResultFragment =
    graphql(`
        fragment EnrollmentAddByBookingIdAddSavedParticipantResultFragment on AddSavedParticipantResult {
            records {
                id
                commaName
                firstName
                middleName
                lastName
                dateOfBirth
            }
            query {
                node(id: $courseId) {
                    __typename
                    ... on Course {
                        savedParticipantsWithWaitlistStatus {
                            customer {
                                id
                            }
                            status
                        }
                    }
                }
            }
        }
    `)
</script>

<script setup lang="ts">
import PrimeText from 'primevue/inputtext'
import type { EnrollmentInputExistingEnrollment } from '~/composables/enrollment-input'
import { computed, ref } from 'vue'
import PrimeButton from 'primevue/button'
import { useRpQuery } from '~/composables/graphql'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { useToast } from 'primevue/usetoast'
import { type FragmentType, getFragmentData } from '~/resources/graphql'
import { useBookingIdName } from '~/composables/use-org'
import { useConfirm } from 'primevue/useconfirm'
import ConfirmDialog from 'primevue/confirmdialog'
import { useFindCustomerByBookingId } from '~/components/PurchaseContract/CustomerPicker/find-customer-by-booking-id'
import { useRouteLink } from '~/composables/routing'
import NuxtLink from '#app/components/nuxt-link'
import type { AddSavedParticipantInput } from '~/resources/graphql/graphql'
import { useWaitlistParticipantEligibilityOptional } from '~/components/PurchaseContract/ParticipantDetails/use-waitlist-participant-eligibility'

const props = defineProps<{
    visible: boolean
    showErrors: boolean
    courseId: string
}>()

const emit = defineEmits<{
    (e: 'addEnrollment', payload: EnrollmentInputExistingEnrollment): void
    (e: 'update:visible', payload: boolean): void
    (e: 'back'): void
}>()

const bookingIdName = useBookingIdName()
const bookingId = ref('')
const bookingIdHelpText = ref(
    'Please enter ' + bookingIdName.value + ' and click Add'
)
const showBookingIdErrors = ref(false)
const bookingIdErrorMessage = computed(() => {
    if (showBookingIdErrors.value || props.showErrors) {
        return bookingIdHelpText.value
    }
    return ''
})

const { profileFriendsAndFamilyLink } = useRouteLink()

const addCustomerByBookingIdLoading = ref(false)
const query = useRpQuery()
const queryClient = useQueryClient()
const { mutate, isPending } = useMutation({
    mutationFn: async (mutationData: AddSavedParticipantInput) => {
        const response = await query(
            graphql(/** @lang GraphQL */ `
                mutation EnrollmentAddByBookingIdAddSavedParticipantMutation(
                    $input: AddSavedParticipantInput!
                    $courseId: ID!
                ) {
                    addSavedParticipant(input: $input) {
                        __typename
                        ... on AddSavedParticipantResult {
                            ...EnrollmentAddByBookingIdAddSavedParticipantResultFragment
                        }
                    }
                }
            `),
            {
                input: mutationData,
                courseId: props.courseId
            }
        )

        const responseType = response.addSavedParticipant.__typename
        if (
            responseType !== 'AddSavedParticipantResult' &&
            responseType !== 'CustomerAlreadySaved' &&
            responseType !== 'CustomerNotFound'
        ) {
            throw new Error()
        }

        return response.addSavedParticipant
    }
})

const toast = useToast()
const confirm = useConfirm()
const findCustomerByBookingId = useFindCustomerByBookingId()
async function handleAddCustomerByBookingId() {
    const bookingIdToAdd = bookingId.value.trim()
    if (bookingIdToAdd === '') {
        showBookingIdErrors.value = true
        bookingIdHelpText.value =
            'Please enter a ' + bookingIdName.value + ' and click Add'
        return
    }

    addCustomerByBookingIdLoading.value = true
    // Find customer
    const data = await findCustomerByBookingId(bookingIdToAdd)

    // If no customer found
    if (data === null) {
        showBookingIdErrors.value = true
        bookingIdHelpText.value =
            'No customer with this ' + bookingIdName.value + ' found'
    } else {
        confirmAndAddSavedParticipant(data.commaName, bookingIdToAdd)
    }

    addCustomerByBookingIdLoading.value = false
}

function confirmAndAddSavedParticipant(
    customerName: string,
    bookingId: string
) {
    confirm.require({
        group: 'enrollmentAddByBookingId',
        message: 'Add ' + customerName + ' to your Family and Friends?',
        header: 'Family and Friends',
        rejectClass: 'bg-white !text-primary-500 hover:bg-white',
        accept: () => {
            return new Promise(resolve => {
                mutate(
                    { customerBookingIds: [bookingId] },
                    {
                        onSuccess: async data => {
                            // This should be covered by the first if-clause above
                            if (data.__typename === 'CustomerNotFound') {
                                throw new Error()
                            }

                            // Customer already saved participant
                            if (data.__typename === 'CustomerAlreadySaved') {
                                showBookingIdErrors.value = true
                                bookingIdHelpText.value =
                                    'Customer is already in your Family and Friends'
                                return
                            }

                            await handleSavedParticipantAdded(data)

                            resolve(void 0)
                        }
                    }
                )
            })
        }
    })
}

const waitlistParticipantEligibility =
    useWaitlistParticipantEligibilityOptional()

async function handleSavedParticipantAdded(
    result: FragmentType<
        typeof EnrollmentAddByBookingIdAddSavedParticipantResultFragment
    >
) {
    // Else successful result
    const resultData = getFragmentData(
        EnrollmentAddByBookingIdAddSavedParticipantResultFragment,
        result
    )

    if (waitlistParticipantEligibility !== null) {
        if (
            resultData.query.node !== null &&
            resultData.query.node.__typename === 'Course'
        ) {
            resultData.query.node.savedParticipantsWithWaitlistStatus.forEach(
                participant => {
                    waitlistParticipantEligibility.savedParticipantsAddedByBookingId.value.set(
                        participant.customer.id,
                        participant.status
                    )
                }
            )
        } else {
            console.log(resultData.query)
            throw new Error()
        }
    }

    // Invalidate the saved participants query from the parent so option list refreshes
    await queryClient.invalidateQueries({
        predicate: query =>
            query.queryKey.includes('ProfileCustomersQuery') ||
            query.queryKey.includes('WaitlistProfileCustomersQuery')
    })

    if (resultData.records.length === 0) {
        throw new Error()
    }

    const record = resultData.records[0]

    // Select the newly added participant
    // End result is having the new customer added as a saved participant and selected
    if (record.dateOfBirth === null) {
        throw new Error()
    }

    emit('addEnrollment', {
        type: 'existing',
        id: record.id,
        commaName: record.commaName,
        firstName: record.firstName,
        middleName: record.middleName,
        lastName: record.lastName,
        dateOfBirth: record.dateOfBirth
    })

    toast.add({
        severity: 'success',
        summary: record.commaName + ' Added',
        life: 5000,
        closable: false
    })

    emit('update:visible', false)
    bookingId.value = ''
    bookingIdHelpText.value =
        'Please enter ' + bookingIdName.value + ' and click Add'
    showBookingIdErrors.value = false
}

function handleCancel() {
    emit('back')
}
</script>
